import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { fmtUGX, shortDateTime } from "@/lib/format";
import { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Wallet, PiggyBank, Lock, Sparkles, ArrowRight, Plus, Users, Heart,
  Calendar, TrendingUp, Shield, Repeat, Zap, Globe, HandCoins, QrCode,
  Copy, Banknote, Target, Bot, ChevronRight, CircleDollarSign, Hash, Share2,
  MessageCircle, Mail, Link2, Receipt, ArrowDownLeft, ArrowUpRight, Send, School,
  CheckCircle2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";
import { TriSpherePayTopUp } from "@/components/schoolos-pay-topup";
import { getWalletStatement, postWalletTransfer, type WalletLedgerEntry } from "@/lib/wallet-ledger.functions";


type Treasury = { id: string; available_ugx: number; reserved_ugx: number; total_ugx: number };
type Pot = { id: string; treasury_id: string; student_id: string | null; kind: string; label: string;
  balance_ugx: number; target_ugx: number | null; target_date: string | null; is_locked: boolean };
type Rule = { id: string; kind: string; amount_ugx: number | null; cadence: string | null;
  day_of_month: number | null; is_active: boolean; pot_id: string | null };
type Contributor = { id: string; name: string; relationship: string | null; phone: string | null;
  amount_ugx: number; last_contribution_at: string | null };
type Handle = { id: string; handle: string; identity_id: string };
type LedgerRow = {
  id: string; kind: string; direction: "credit" | "debit"; title: string; subtitle: string | null;
  amount_ugx: number; ref: string | null; occurred_at: string; student_id: string | null;
};

type Student = { id: string; full_name: string; initials: string; color_hex: string; balance_ugx?: number };
type Fee = { student_id: string; total_ugx: number; paid_ugx: number };

const potIcon: Record<string, any> = {
  fees_escrow: Lock, lunch: HandCoins, transport: ArrowRight,
  emergency: Shield, savings: PiggyBank, trip: Globe, exam: Target, custom: CircleDollarSign,
};

export function FamilyTreasury({
  walletId,
  students = [],
  fees = [],
  parentBalance = 0,
}: {
  walletId?: string;
  students?: Student[];
  fees?: Fee[];
  parentBalance?: number;
} = {}) {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: treasury } = useQuery({
    queryKey: ["treasury", user?.id],
    queryFn: async () => {
      let { data } = await (supabase.from as any)("family_treasuries").select("*").eq("guardian_id", user!.id).maybeSingle();
      if (!data) {
        await supabase.rpc("ensure_family_treasury" as any, { _uid: user!.id });
        const r = await (supabase.from as any)("family_treasuries").select("*").eq("guardian_id", user!.id).maybeSingle();
        data = r.data;
      }
      return data as Treasury | null;
    },
  });

  const { data: pots = [] } = useQuery({
    queryKey: ["pots", treasury?.id], enabled: !!treasury?.id,
    queryFn: async () => (await (supabase.from as any)("treasury_pots").select("*").eq("treasury_id", treasury!.id).order("created_at")).data as Pot[] ?? [],
  });
  const { data: rules = [] } = useQuery({
    queryKey: ["rules", treasury?.id], enabled: !!treasury?.id,
    queryFn: async () => (await (supabase.from as any)("treasury_rules").select("*").eq("treasury_id", treasury!.id)).data as Rule[] ?? [],
  });
  const { data: contributors = [] } = useQuery({
    queryKey: ["contributors", treasury?.id], enabled: !!treasury?.id,
    queryFn: async () => (await (supabase.from as any)("treasury_contributors").select("*").eq("treasury_id", treasury!.id).order("amount_ugx", { ascending: false })).data as Contributor[] ?? [],
  });

  const { data: handles = [] } = useQuery({
    queryKey: ["my-payids", user?.id],
    queryFn: async () => {
      const { data: studentRows } = await (supabase.from as any)("students").select("id").eq("guardian_id", user!.id);
      const ids = (studentRows ?? []).map((s: any) => s.id);
      if (!ids.length) return [];
      const { data: idents } = await (supabase.from as any)("financial_identities").select("id, student_id, sid").in("student_id", ids);
      const identIds = (idents ?? []).map((i: any) => i.id);
      if (!identIds.length) return [];
      return (await (supabase.from as any)("payid_handles").select("*").in("identity_id", identIds)).data as Handle[] ?? [];
    },
  });

  // Statement powered by the wallet ledger (server computes running balance & reconciliation)
  const fetchStatement = useServerFn(getWalletStatement);
  const [stmtSearch, setStmtSearch] = useState("");
  const [stmtDir, setStmtDir] = useState<"all" | "credit" | "debit">("all");
  const [stmtKid, setStmtKid] = useState<string>("all");
  const { data: statement } = useQuery({
    queryKey: ["wallet-statement", user?.id, walletId ?? null, stmtSearch, stmtDir, stmtKid],
    queryFn: () => fetchStatement({ data: {
      walletId, limit: 500,
      search: stmtSearch || undefined,
      direction: stmtDir,
      studentId: stmtKid === "all" ? null : stmtKid,
    } }),
    enabled: !!user?.id,
  });


  const moveToReserve = useMutation({
    mutationFn: async ({ potId, amount }: { potId: string; amount: number }) => {
      if (!treasury) return;
      const pot = pots.find(p => p.id === potId);
      if (!pot) return;
      if (treasury.available_ugx < amount) throw new Error("Not enough available");
      await supabase.from("family_treasuries" as any).update({
        available_ugx: treasury.available_ugx - amount,
        reserved_ugx: treasury.reserved_ugx + amount,
      }).eq("id", treasury.id);
      await supabase.from("treasury_pots" as any).update({ balance_ugx: pot.balance_ugx + amount }).eq("id", potId);
    },
    onSuccess: () => { qc.invalidateQueries(); toast.success("Reserved into pot"); },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleRule = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      await supabase.from("treasury_rules" as any).update({ is_active: active }).eq("id", id);
    },
    onSuccess: () => qc.invalidateQueries(),
  });

  const projection = useMemo(() => {
    const weekly = rules.find(r => r.kind === "micro_savings")?.amount_ugx ?? 0;
    const monthly = rules.find(r => r.kind === "salary_linked")?.amount_ugx ?? 0;
    const perMonth = weekly * 4 + monthly;
    const targets = pots.filter(p => p.target_ugx).reduce((s, p) => s + (p.target_ugx! - p.balance_ugx), 0);
    const monthsTo = perMonth ? Math.ceil(targets / perMonth) : null;
    return { perMonth, targets, monthsTo };
  }, [rules, pots]);

  const combinedBalance = (treasury?.available_ugx ?? 0) + parentBalance;
  const primaryHandle = handles[0]?.handle ?? null;

  if (!treasury) return <Card className="p-8 text-center text-muted-foreground">Preparing your Family Wallet…</Card>;

  return (
    <div className="space-y-6">
      {/* HERO */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="overflow-hidden border-0 bg-gradient-to-br from-[#7B1416] via-[#163a2c] to-[#0a261d] text-primary-foreground shadow-card">
          <div className="grid gap-6 p-6 sm:p-8 md:grid-cols-[1.4fr_1fr]">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] opacity-80">
                <Wallet className="h-3.5 w-3.5" /> Family Wallet · powered by TriSphere Pay
              </div>
              <div className="mt-2 font-display text-5xl tabular-nums">{fmtUGX(combinedBalance)}</div>
              <div className="mt-1 text-sm opacity-80">
                Available {fmtUGX(treasury.available_ugx + parentBalance)} · Reserved in pots {fmtUGX(treasury.reserved_ugx)}
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <TriSpherePayTopUp
                  walletId={walletId}
                  students={students}
                  fees={fees}
                  trigger={
                    <Button size="sm" className="bg-white text-[#7B1416] hover:bg-white/90">
                      <Plus className="mr-1.5 h-4 w-4" />Top up via TriSphere Pay
                    </Button>
                  }
                />
                {primaryHandle && (
                  <SharePayIdDialog
                    handle={primaryHandle}
                    parentName={user?.user_metadata?.full_name ?? "Family"}
                    trigger={
                      <Button size="sm" variant="secondary" className="bg-white/15 text-primary-foreground hover:bg-white/25 border-0">
                        <Share2 className="mr-1.5 h-4 w-4" />Share PayID
                      </Button>
                    }
                  />
                )}
                <Button size="sm" variant="secondary" className="bg-white/15 text-primary-foreground hover:bg-white/25 border-0">
                  <Bot className="mr-1.5 h-4 w-4" />AI Copilot
                </Button>
              </div>
            </div>

            <div className="space-y-3 rounded-2xl bg-white/10 p-4 backdrop-blur">
              <div className="flex items-center justify-between text-xs uppercase tracking-wide opacity-80">
                <span>One rail. Every spend.</span>
                <Badge className="bg-[var(--mustard)]/90 text-foreground hover:bg-[var(--mustard)]">TriSphere Pay</Badge>
              </div>
              <div className="text-sm leading-relaxed">
                Tuition, transport, lunch, marketplace — all draw from this wallet. Top-ups, allocations, and
                receipts post here automatically with a PRN and a per-line WTX.
              </div>
              <div className="grid grid-cols-3 gap-2 pt-2 text-center text-xs">
                <div className="rounded-lg bg-white/10 p-2">
                  <div className="font-display text-base">{pots.length}</div><div className="opacity-80">active pots</div>
                </div>
                <div className="rounded-lg bg-white/10 p-2">
                  <div className="font-display text-base">{contributors.length}</div><div className="opacity-80">family contributors</div>
                </div>
                <div className="rounded-lg bg-white/10 p-2">
                  <div className="font-display text-base">{projection.monthsTo ?? "—"}</div><div className="opacity-80">months to goals</div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      <Tabs defaultValue="statement" className="space-y-4">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="statement"><Receipt className="mr-1.5 h-4 w-4" />Statement</TabsTrigger>
          <TabsTrigger value="transfers"><Send className="mr-1.5 h-4 w-4" />Transfers</TabsTrigger>
          <TabsTrigger value="pots"><PiggyBank className="mr-1.5 h-4 w-4" />Pots</TabsTrigger>
          <TabsTrigger value="rules"><Repeat className="mr-1.5 h-4 w-4" />Auto-rules</TabsTrigger>
          <TabsTrigger value="payid"><Hash className="mr-1.5 h-4 w-4" />PayID & Share</TabsTrigger>
          <TabsTrigger value="contributors"><Users className="mr-1.5 h-4 w-4" />Family & Friends</TabsTrigger>
          <TabsTrigger value="ai"><Sparkles className="mr-1.5 h-4 w-4" />AI Copilot</TabsTrigger>
        </TabsList>

        {/* TRANSFERS */}
        <TabsContent value="transfers" className="space-y-3">
          <TransfersPanel
            treasury={treasury}
            students={students}
            fees={fees}
            onDone={() => qc.invalidateQueries()}
            guardianId={user?.id ?? ""}
            parentWalletId={walletId}
          />
        </TabsContent>

        {/* STATEMENT */}
        <TabsContent value="statement" className="space-y-3">
          <StatementPanel
            statement={statement ?? null}
            students={students}
            search={stmtSearch} onSearch={setStmtSearch}
            direction={stmtDir} onDirection={setStmtDir}
            kid={stmtKid} onKid={setStmtKid}
          />
        </TabsContent>


        {/* POTS */}
        <TabsContent value="pots" className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {pots.map((p) => {
              const Icon = potIcon[p.kind] ?? CircleDollarSign;
              const pct = p.target_ugx ? Math.min(100, (p.balance_ugx / p.target_ugx) * 100) : null;
              return (
                <Card key={p.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold">{p.label}</div>
                        <div className="text-xs capitalize text-muted-foreground">{p.kind.replace("_", " ")}</div>
                      </div>
                    </div>
                    {p.is_locked && <Badge variant="secondary"><Lock className="mr-1 h-3 w-3" />Escrow</Badge>}
                  </div>
                  <div className="mt-3 font-display text-2xl tabular-nums">{fmtUGX(p.balance_ugx)}</div>
                  {pct !== null && (
                    <>
                      <Progress value={pct} className="mt-2 h-1.5" />
                      <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                        <span>{Math.round(pct)}% of {fmtUGX(p.target_ugx!)}</span>
                        {p.target_date && <span>by {new Date(p.target_date).toLocaleDateString("en-UG", { month: "short", day: "numeric" })}</span>}
                      </div>
                    </>
                  )}
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => moveToReserve.mutate({ potId: p.id, amount: 50000 })}>
                      <Plus className="mr-1 h-3.5 w-3.5" />Reserve UGX 50k
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* RULES */}
        <TabsContent value="rules" className="space-y-3">
          <Card className="p-5">
            <div className="mb-3 font-display text-lg">Automate everything</div>
            <div className="space-y-3">
              {rules.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                  <div className="flex items-center gap-3">
                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-secondary">
                      {r.kind === "salary_linked" && <Calendar className="h-4 w-4" />}
                      {r.kind === "round_up" && <Zap className="h-4 w-4" />}
                      {r.kind === "micro_savings" && <TrendingUp className="h-4 w-4" />}
                      {r.kind === "auto_pay" && <Banknote className="h-4 w-4" />}
                      {r.kind === "auto_budget" && <Target className="h-4 w-4" />}
                    </div>
                    <div>
                      <div className="text-sm font-semibold capitalize">{r.kind.replace("_", " ")}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.kind === "salary_linked" && `Reserve ${fmtUGX(r.amount_ugx ?? 0)} on day ${r.day_of_month} of each month`}
                        {r.kind === "round_up" && "Round every top-up up to nearest UGX 5,000 into savings"}
                        {r.kind === "micro_savings" && `Save ${fmtUGX(r.amount_ugx ?? 0)} ${r.cadence}`}
                        {r.kind === "auto_pay" && "Pay invoices automatically when issued"}
                        {r.kind === "auto_budget" && `Reserve ${fmtUGX(r.amount_ugx ?? 0)} ${r.cadence}`}
                      </div>
                    </div>
                  </div>
                  <Switch checked={r.is_active} onCheckedChange={(v) => toggleRule.mutate({ id: r.id, active: v })} />
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* PAYID */}
        <TabsContent value="payid" className="space-y-3">
          <Card className="p-5">
            <div className="mb-1 font-display text-lg">Your PayIDs</div>
            <div className="mb-4 text-sm text-muted-foreground">
              Share one handle — anyone on MTN, Airtel, bank, or diaspora can top up your Family Wallet. No long references, no fees lost in translation.
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {handles.length === 0 && <div className="text-sm text-muted-foreground">No PayIDs yet.</div>}
              {handles.map((h) => (
                <div key={h.id} className="flex items-center justify-between rounded-xl border bg-gradient-to-br from-[var(--forest)]/8 to-transparent p-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">PayID</div>
                    <div className="mt-0.5 font-display text-2xl">@{h.handle}</div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText("@" + h.handle); toast.success("PayID copied"); }}>
                      <Copy className="mr-1.5 h-3.5 w-3.5" />Copy
                    </Button>
                    <SharePayIdDialog
                      handle={h.handle}
                      parentName={user?.user_metadata?.full_name ?? "Family"}
                      trigger={<Button size="sm" className="bg-[var(--forest)] text-primary-foreground hover:opacity-90"><Share2 className="mr-1.5 h-3.5 w-3.5" />Share</Button>}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* CONTRIBUTORS / FAMILY & FRIENDS */}
        <TabsContent value="contributors" className="space-y-3">
          <Card className="overflow-hidden p-0">
            <div className="flex items-start justify-between gap-3 border-b bg-gradient-to-br from-[var(--ochre)]/10 to-transparent p-5">
              <div>
                <div className="flex items-center gap-2 font-display text-lg"><Heart className="h-5 w-5 text-[var(--terracotta)]" />Family & friends top-up</div>
                <p className="mt-1 max-w-lg text-sm text-muted-foreground">
                  Grandparents, uncles, sponsors, diaspora — anyone can chip in. They send via MTN, Airtel, bank, or
                  card to your PayID and the amount lands in this Family Wallet instantly.
                </p>
              </div>
              {primaryHandle && (
                <SharePayIdDialog
                  handle={primaryHandle}
                  parentName={user?.user_metadata?.full_name ?? "Family"}
                  trigger={<Button size="sm" className="bg-[var(--forest)] text-primary-foreground hover:opacity-90"><Send className="mr-1.5 h-3.5 w-3.5" />Invite to chip in</Button>}
                />
              )}
            </div>

            <div className="divide-y">
              {contributors.length === 0 && (
                <div className="px-5 py-10 text-center text-sm text-muted-foreground">
                  No contributors yet — share your PayID to invite family.
                </div>
              )}
              {contributors.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-full bg-[var(--forest)]/10 text-sm font-semibold text-[var(--forest)]">
                      {c.name.split(" ").map(s => s[0]).slice(0, 2).join("")}
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{c.name}</div>
                      <div className="text-xs capitalize text-muted-foreground">{c.relationship ?? "—"} · {c.phone ?? "—"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-sm font-semibold tabular-nums">{fmtUGX(c.amount_ugx)}</div>
                      <div className="text-[10px] text-muted-foreground">{c.last_contribution_at ? shortDateTime(c.last_contribution_at) : "—"}</div>
                    </div>
                    {primaryHandle && (
                      <Button size="sm" variant="outline"
                        onClick={() => {
                          const msg = encodeURIComponent(
                            `Hi ${c.name.split(" ")[0]}, here's our TriSphere PayID for the kids: @${primaryHandle}. Any amount helps 🙏`
                          );
                          const phone = (c.phone ?? "").replace(/[^\d]/g, "");
                          window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
                        }}
                      >
                        <MessageCircle className="mr-1.5 h-3.5 w-3.5" />Nudge
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t p-4">
              <InviteContributorDialog treasuryId={treasury.id} />
            </div>
          </Card>
        </TabsContent>

        {/* AI */}
        <TabsContent value="ai">
          <Card className="p-5">
            <div className="mb-1 flex items-center gap-2 font-display text-lg"><Bot className="h-5 w-5 text-primary" />AI Copilot</div>
            <div className="space-y-3">
              <Insight icon={TrendingUp} title="On track for Term 3"
                body={`At UGX ${(projection.perMonth/1000).toFixed(0)}k/month, you'll fund all pots in ~${projection.monthsTo ?? "—"} months.`} />
              <Insight icon={Zap} title="Salary day boost"
                body="On day 28, UGX 300,000 will move automatically into your savings pot. Want to bump it to UGX 350,000?" action="Bump" />
              <Insight icon={Shield} title="Emergency low"
                body="Your emergency pot is below 25% of target. Recommend reserving UGX 30,000 this week." action="Reserve" />
              {primaryHandle && (
                <Insight icon={Share2} title="Ask Auntie Joyce to chip in"
                  body={`She topped up UGX 450k last month. A quick WhatsApp with your PayID @${primaryHandle} usually closes the gap.`} action="Send" />
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Insight({ icon: Icon, title, body, action }: { icon: any; title: string; body: string; action?: string }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border bg-gradient-to-br from-primary/5 to-transparent p-3">
      <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" /></div>
      <div className="flex-1">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-muted-foreground">{body}</div>
      </div>
      {action && <Button size="sm" variant="outline">{action}<ChevronRight className="ml-1 h-3.5 w-3.5" /></Button>}
    </div>
  );
}

/* ============================================================
   Share PayID dialog — QR + WhatsApp + SMS + Email + copy link
============================================================ */
function SharePayIdDialog({
  handle, parentName, trigger,
}: { handle: string; parentName: string; trigger: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [qr, setQr] = useState<string>("");
  const [amount, setAmount] = useState("100000");
  const [note, setNote] = useState(`Help top up the kids' TriSphere wallet 🙏`);
  const [purpose, setPurpose] = useState<"any" | "fees" | "lunch" | "transport" | "birthday">("any");

  const link = useMemo(() => {
    const base = typeof window !== "undefined" ? window.location.origin : "https://schoolos.app";
    return `${base}/pay/@${handle}?amt=${amount}&for=${purpose}`;
  }, [handle, amount, purpose]);

  useEffect(() => {
    if (!open) return;
    QRCode.toDataURL(link, { width: 320, margin: 1, color: { dark: "#7B1416", light: "#FFFFFF" } })
      .then(setQr).catch(() => setQr(""));
  }, [open, link]);

  const purposeCopy: Record<string, string> = {
    any: "anywhere it helps", fees: "school fees", lunch: "lunch & canteen",
    transport: "bus & transport", birthday: "a birthday gift 🎂",
  };
  const message = `${note}\n\nYou're chipping in to ${purposeCopy[purpose]}.\nTriSphere PayID: @${handle}\nSuggested: UGX ${Number(amount).toLocaleString()}\nTap to top up: ${link}\n\nThank you,\n${parentName}\n— via TriSphere Pay`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl overflow-hidden p-0">
        {/* HERO */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#7B1416] via-[#163a2c] to-[#0a261d] p-6 text-white">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[var(--mustard)]/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-[var(--terracotta)]/20 blur-3xl" />
          <div className="relative flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] opacity-80"><Heart className="h-3 w-3 text-[var(--terracotta)]" />It takes a village</div>
              <DialogTitle className="mt-2 font-display text-2xl">Invite family & friends to chip in</DialogTitle>
              <DialogDescription className="mt-1 text-sm text-white/70">
                Grandparents, aunties, sponsors, diaspora — they tap one link and top up via MTN, Airtel, bank, card, or any agent. Money lands in your Family Wallet instantly.
              </DialogDescription>
            </div>
            <Badge className="bg-white/15 text-white hover:bg-white/15 text-[10px]">TriSphere Pay</Badge>
          </div>
        </div>

        {/* BODY */}
        <div className="grid gap-5 p-6 md:grid-cols-[240px_1fr]">
          <div className="flex flex-col items-center gap-3">
            <div className="rounded-2xl border-2 border-[var(--forest)]/15 bg-white p-3 shadow-sm">
              {qr ? <img src={qr} alt={`@${handle} QR`} className="h-[200px] w-[200px]" />
                  : <div className="grid h-[200px] w-[200px] place-items-center text-muted-foreground"><QrCode className="h-10 w-10" /></div>}
            </div>
            <div className="rounded-full border bg-card px-3 py-1 font-mono text-sm">@{handle}</div>
            <Button size="sm" variant="ghost" className="-mt-1 h-7 text-[11px]"
              onClick={() => { navigator.clipboard.writeText("@" + handle); toast.success("PayID copied"); }}>
              <Copy className="mr-1 h-3 w-3" />Copy handle
            </Button>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">What is it for?</Label>
              <div className="flex flex-wrap gap-1.5">
                {([
                  ["any","Anywhere"],["fees","School fees"],["lunch","Lunch"],["transport","Transport"],["birthday","Birthday 🎂"],
                ] as const).map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setPurpose(k)}
                    className={`rounded-full border px-3 py-1 text-xs transition ${purpose === k ? "border-[var(--forest)] bg-[var(--forest)] text-primary-foreground" : "bg-card hover:bg-muted"}`}>{l}</button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Suggested amount</Label>
              <div className="flex items-center gap-2">
                <span className="rounded-md border bg-muted/40 px-2 py-2 text-xs font-mono text-muted-foreground">UGX</span>
                <Input value={Number(amount).toLocaleString()} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} className="font-mono text-base" />
              </div>
              <div className="flex flex-wrap gap-1 pt-1">
                {[50000, 100000, 200000, 500000, 1000000].map(v => (
                  <button key={v} type="button" onClick={() => setAmount(String(v))}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] transition ${Number(amount) === v ? "border-[var(--forest)] bg-[var(--forest)]/10 text-[var(--forest)]" : "hover:bg-muted"}`}>
                    {fmtUGX(v)}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Personal note</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="resize-none text-sm" />
            </div>

            <div className="rounded-xl border bg-gradient-to-br from-muted/40 to-transparent p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Preview</div>
              <pre className="mt-1 whitespace-pre-wrap font-sans text-[11px] leading-relaxed text-foreground">{message}</pre>
            </div>
          </div>
        </div>

        <DialogFooter className="grid grid-cols-2 gap-2 border-t bg-card/60 p-4 sm:grid-cols-4">
          <Button className="bg-[#25D366] text-white hover:opacity-90"
            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank")}>
            <MessageCircle className="mr-1.5 h-4 w-4" />WhatsApp
          </Button>
          <Button variant="outline" onClick={() => window.open(`sms:?body=${encodeURIComponent(message)}`)}>
            <Send className="mr-1.5 h-4 w-4" />SMS
          </Button>
          <Button variant="outline"
            onClick={() => window.open(`mailto:?subject=${encodeURIComponent("Top up our TriSphere wallet")}&body=${encodeURIComponent(message)}`)}>
            <Mail className="mr-1.5 h-4 w-4" />Email
          </Button>
          <Button variant="outline" onClick={() => { navigator.clipboard.writeText(link); toast.success("Top-up link copied"); }}>
            <Copy className="mr-1.5 h-4 w-4" />Copy link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   Invite contributor — add to ledger of family helpers
============================================================ */
function InviteContributorDialog({ treasuryId }: { treasuryId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("grandparent");
  const [phone, setPhone] = useState("");

  const save = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error("Add a name");
      await supabase.from("treasury_contributors" as any).insert({
        treasury_id: treasuryId, name: name.trim(), relationship, phone: phone.trim() || null, amount_ugx: 0,
      });
    },
    onSuccess: () => { qc.invalidateQueries(); toast.success("Contributor added"); setOpen(false); setName(""); setPhone(""); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-full" variant="outline"><Plus className="mr-1.5 h-4 w-4" />Add a family contributor</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add a family contributor</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Auntie Joyce" /></div>
          <div className="space-y-1.5">
            <Label>Relationship</Label>
            <select className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={relationship} onChange={(e) => setRelationship(e.target.value)}>
              <option value="grandparent">Grandparent</option>
              <option value="uncle">Uncle / Auntie</option>
              <option value="sibling">Sibling</option>
              <option value="diaspora">Diaspora</option>
              <option value="sponsor">Sponsor</option>
              <option value="friend">Family friend</option>
            </select>
          </div>
          <div className="space-y-1.5"><Label>WhatsApp / phone</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+256 7XX XXX XXX" /></div>
        </div>
        <DialogFooter>
          <Button className="w-full" onClick={() => save.mutate()} disabled={save.isPending}>Add contributor</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   STATEMENT — searchable, filterable, CSV export
============================================================ */
function StatementPanel({
  statement, students, search, onSearch, direction, onDirection, kid, onKid,
}: {
  statement: import("@/lib/wallet-ledger.functions").WalletStatement | null;
  students: Student[];
  search: string; onSearch: (v: string) => void;
  direction: "all" | "credit" | "debit"; onDirection: (v: "all" | "credit" | "debit") => void;
  kid: string; onKid: (v: string) => void;
}) {
  const entries = statement?.entries ?? [];
  const credits = statement?.total_credits_ugx ?? 0;
  const debits = statement?.total_debits_ugx ?? 0;
  const cached = statement?.cached_balance_ugx ?? 0;
  const computed = statement?.computed_balance_ugx ?? 0;
  const reconciled = statement ? cached === computed : true;

  const exportCsv = () => {
    const head = "Date,Direction,Kind,Title,Detail,Amount UGX,Running Balance,Reference\n";
    const body = entries.map(r => [
      new Date(r.occurred_at).toISOString(),
      r.direction, r.kind, JSON.stringify(r.title), JSON.stringify(r.subtitle ?? ""),
      (r.direction === "credit" ? "" : "-") + r.amount_ugx,
      r.running_balance_ugx,
      r.ref ?? "",
    ].join(",")).join("\n");
    const blob = new Blob([head + body], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `schoolos-statement-${Date.now()}.csv`; a.click();
    toast.success("Statement exported");
  };

  return (
    <Card className="overflow-hidden p-0">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-gradient-to-br from-[var(--forest)]/5 to-transparent px-5 py-3">
        <div>
          <div className="font-display text-base">Wallet statement</div>
          <div className="text-[11px] text-muted-foreground">
            Every line posted by the wallet ledger · running balance computed server-side · WTX-traceable
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <div className={`flex items-center gap-1 rounded-md px-2 py-1 ${reconciled ? "bg-[var(--forest)]/10 text-[var(--forest)]" : "bg-amber-500/15 text-amber-700"}`}>
            {reconciled ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
            Ledger {reconciled ? "balanced" : "drift"} · {fmtUGX(cached)} cached / {fmtUGX(computed)} computed
          </div>
          <div className="rounded-md bg-[var(--forest)]/10 px-2 py-1 text-[var(--forest)]">In <span className="font-semibold">{fmtUGX(credits)}</span></div>
          <div className="rounded-md bg-muted px-2 py-1">Out <span className="font-semibold">{fmtUGX(debits)}</span></div>
          <Button size="sm" variant="outline" onClick={exportCsv}><Receipt className="mr-1.5 h-3.5 w-3.5" />Export CSV</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 border-b bg-card/40 px-5 py-2">
        <Input value={search} onChange={e => onSearch(e.target.value)} placeholder="Search title, detail, WTX/PRN…" className="h-8 max-w-xs text-xs" />
        <div className="flex gap-1">
          {(["all","credit","debit"] as const).map(d => (
            <button key={d} onClick={() => onDirection(d)}
              className={`rounded-full border px-2.5 py-0.5 text-[11px] capitalize ${direction === d ? "border-foreground bg-foreground text-background" : "hover:bg-muted"}`}>{d === "credit" ? "Money in" : d === "debit" ? "Money out" : "All"}</button>
          ))}
        </div>
        {students.length > 0 && (
          <select value={kid} onChange={e => onKid(e.target.value)}
            className="h-8 rounded-md border bg-background px-2 text-xs">
            <option value="all">All children</option>
            {students.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </select>
        )}
      </div>
      <div className="divide-y">
        {entries.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground">No matching activity.</div>
        )}
        {entries.map((row) => {
          const credit = row.direction === "credit";
          const Icon = credit ? ArrowDownLeft : ArrowUpRight;
          return (
            <div key={row.id} className="flex items-center gap-3 px-5 py-3">
              <div className={`grid h-9 w-9 place-items-center rounded-lg ${credit ? "bg-[var(--forest)]/10 text-[var(--forest)]" : "bg-muted text-muted-foreground"}`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{row.title}</div>
                <div className="truncate text-[11px] text-muted-foreground">{row.subtitle ?? row.kind} · {shortDateTime(row.occurred_at)}</div>
              </div>
              <div className="text-right">
                <div className={`text-sm font-semibold tabular-nums ${credit ? "text-[var(--forest)]" : "text-foreground"}`}>
                  {credit ? "+" : "−"}{fmtUGX(row.amount_ugx)}
                </div>
                <div className="text-[10px] tabular-nums text-muted-foreground">bal {fmtUGX(row.running_balance_ugx)}</div>
                {row.ref && <div className="font-mono text-[10px] text-muted-foreground">{row.ref}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}


/* ============================================================
   TRANSFERS — wallet → child, fees, child → child
============================================================ */
function TransfersPanel({
  treasury, students, fees, onDone, guardianId, parentWalletId,
}: {
  treasury: Treasury; students: Student[]; fees: Fee[]; onDone: () => void; guardianId: string;
  parentWalletId?: string;
}) {
  const [mode, setMode] = useState<"child" | "fees" | "between">("child");
  const [from, setFrom] = useState<string>(students[0]?.id ?? "");
  const [to, setTo] = useState<string>(students[1]?.id ?? students[0]?.id ?? "");
  const [amount, setAmount] = useState<string>("50000");
  const amt = Math.max(0, Number(amount.replace(/\D/g, "")) || 0);
  const transferFn = useServerFn(postWalletTransfer);

  // Resolve student → wallet via student_cards
  const { data: studentWallets = {} } = useQuery({
    queryKey: ["student-wallets", students.map(s => s.id).join(",")],
    enabled: students.length > 0,
    queryFn: async () => {
      const ids = students.map(s => s.id);
      const { data } = await (supabase.from as any)("student_cards").select("student_id, wallet_id").in("student_id", ids);
      const map: Record<string, string> = {};
      (data ?? []).forEach((r: any) => { if (r.wallet_id) map[r.student_id] = r.wallet_id; });
      return map;
    },
  });

  const move = useMutation({
    mutationFn: async () => {
      if (amt <= 0) throw new Error("Enter an amount");
      if (!parentWalletId) throw new Error("Family wallet not ready");

      if (mode === "child") {
        const child = students.find(s => s.id === to);
        if (!child) throw new Error("Pick a child");
        const childWallet = studentWallets[child.id];
        if (!childWallet) throw new Error("This child has no wallet yet");
        await transferFn({ data: {
          fromWalletId: parentWalletId,
          toWalletId: childWallet,
          amount: amt,
          kind: "transfer",
          titleOut: `Sent to ${child.full_name.split(" ")[0]}'s wallet`,
          titleIn:  `From guardian · Family Wallet`,
          subtitle: "TriSphere Pay · instant",
          studentId: child.id,
        } });
      } else if (mode === "fees") {
        const fee = fees.find(f => f.student_id === to);
        const child = students.find(s => s.id === to);
        if (!fee || !child) throw new Error("Pick a child with active fees");
        // Debit-only entry against parent wallet; school AR is tracked in student_fee_terms
        await transferFn({ data: {
          fromWalletId: parentWalletId,
          toWalletId: null,
          amount: amt,
          kind: "fees",
          titleOut: `Fee payment · ${child.full_name.split(" ")[0]}`,
          titleIn: "",
          subtitle: "Term installment via TriSphere Pay",
          studentId: child.id,
          sourceType: "student_fee_terms",
        } });
        const newPaid = Math.min(fee.total_ugx, fee.paid_ugx + amt);
        await supabase.from("student_fee_terms" as any).update({ paid_ugx: newPaid }).eq("student_id", child.id);
      } else {
        const a = students.find(s => s.id === from);
        const b = students.find(s => s.id === to);
        if (!a || !b || a.id === b.id) throw new Error("Pick two different children");
        const aw = studentWallets[a.id]; const bw = studentWallets[b.id];
        if (!aw || !bw) throw new Error("One of the children has no wallet yet");
        await transferFn({ data: {
          fromWalletId: aw,
          toWalletId: bw,
          amount: amt,
          kind: "transfer",
          titleOut: `Moved to ${b.full_name.split(" ")[0]}`,
          titleIn:  `Received from ${a.full_name.split(" ")[0]}`,
          subtitle: "Between children",
          studentId: b.id,
        } });
      }
    },
    onSuccess: () => { toast.success("Transfer complete · receipt posted to statement"); setAmount("50000"); onDone(); },
    onError: (e: Error) => toast.error(e.message),
  });


  const modes: { k: typeof mode; label: string; sub: string; icon: any; tone: string }[] = [
    { k: "child", label: "Send to child", sub: "Top up a kid's wallet", icon: Send, tone: "var(--forest)" },
    { k: "fees", label: "Pay school fees", sub: "Reduce outstanding term balance", icon: School, tone: "var(--terracotta)" },
    { k: "between", label: "Move between kids", sub: "Re-balance siblings", icon: Repeat, tone: "var(--mustard)" },
  ];

  const targetFee = fees.find(f => f.student_id === to);
  const targetChild = students.find(s => s.id === to);
  const sourceChild = students.find(s => s.id === from);

  return (
    <div className="grid gap-3 md:grid-cols-[260px_1fr]">
      <div className="space-y-2">
        {modes.map(m => {
          const active = mode === m.k;
          return (
            <button key={m.k} onClick={() => setMode(m.k)}
              className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition ${active ? "border-foreground bg-card shadow-sm" : "hover:bg-muted/40"}`}>
              <span className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: `color-mix(in oklab, ${m.tone} 14%, transparent)`, color: m.tone }}>
                <m.icon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold">{m.label}</div>
                <div className="text-[11px] text-muted-foreground">{m.sub}</div>
              </div>
            </button>
          );
        })}
        <div className="rounded-xl border bg-gradient-to-br from-[var(--forest)]/8 to-transparent p-3 text-[11px]">
          <div className="text-muted-foreground">Available in Family Wallet</div>
          <div className="mt-0.5 font-display text-xl">{fmtUGX(treasury.available_ugx)}</div>
        </div>
      </div>

      <Card className="p-5">
        <div className="grid gap-4 md:grid-cols-2">
          {mode === "between" && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">From child</Label>
              <select value={from} onChange={e => setFrom(e.target.value)} className="h-10 w-full rounded-md border bg-background px-3 text-sm">
                {students.map(s => <option key={s.id} value={s.id}>{s.full_name} · {fmtUGX(s.balance_ugx ?? 0)}</option>)}
              </select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{mode === "between" ? "To child" : mode === "fees" ? "Pay fees for" : "Top up child"}</Label>
            <select value={to} onChange={e => setTo(e.target.value)} className="h-10 w-full rounded-md border bg-background px-3 text-sm">
              {students.map(s => <option key={s.id} value={s.id}>{s.full_name}{mode === "fees" && fees.find(f => f.student_id === s.id) ? ` · owes ${fmtUGX(fees.find(f => f.student_id === s.id)!.total_ugx - fees.find(f => f.student_id === s.id)!.paid_ugx)}` : ""}</option>)}
            </select>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Amount</Label>
            <div className="flex items-center gap-2">
              <span className="rounded-md border bg-muted/40 px-2 py-2 text-xs font-mono text-muted-foreground">UGX</span>
              <Input value={Number(amt).toLocaleString()} onChange={e => setAmount(e.target.value)} className="font-mono text-base" />
            </div>
            <div className="flex flex-wrap gap-1 pt-1">
              {[20000, 50000, 100000, 245000, 500000].map(v => (
                <button key={v} type="button" onClick={() => setAmount(String(v))}
                  className={`rounded-full border px-2.5 py-0.5 text-[11px] ${amt === v ? "border-[var(--forest)] bg-[var(--forest)]/10 text-[var(--forest)]" : "hover:bg-muted"}`}>{fmtUGX(v)}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="mt-4 rounded-xl border bg-gradient-to-br from-muted/30 to-transparent p-4 text-sm">
          {mode === "child" && targetChild && (
            <div>Sending <b>{fmtUGX(amt)}</b> from your Family Wallet to <b>{targetChild.full_name}</b>'s student wallet. New balance: <b>{fmtUGX((targetChild.balance_ugx ?? 0) + amt)}</b>.</div>
          )}
          {mode === "fees" && targetChild && targetFee && (
            <div>
              Paying <b>{fmtUGX(amt)}</b> toward {targetChild.full_name}'s term fees.<br />
              Outstanding before: {fmtUGX(targetFee.total_ugx - targetFee.paid_ugx)} → after: <b>{fmtUGX(Math.max(0, targetFee.total_ugx - targetFee.paid_ugx - amt))}</b>.
            </div>
          )}
          {mode === "between" && sourceChild && targetChild && (
            <div>Moving <b>{fmtUGX(amt)}</b> from {sourceChild.full_name} ({fmtUGX(sourceChild.balance_ugx ?? 0)}) to <b>{targetChild.full_name}</b> ({fmtUGX(targetChild.balance_ugx ?? 0)}).</div>
          )}
          <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
            <Shield className="h-3 w-3" /> Settled instantly via TriSphere Pay · receipt posts to your statement with a WTX reference.
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button onClick={() => move.mutate()} disabled={move.isPending || amt <= 0}
            className="bg-[var(--forest)] text-primary-foreground hover:opacity-90">
            <Send className="mr-1.5 h-4 w-4" />
            {mode === "fees" ? "Pay fees now" : mode === "between" ? "Move funds" : "Send to child"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

